import { ComponentInternalInstance, getCurrentInstance, reactive, ref, Ref } from '@vue/composition-api';
import { UploadFile } from 'tdesign-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import tips from '@/renderer/scripts/utils/tips';
import getRequest from '@/renderer/scripts/httpRequest';
import smh, { SMH } from '@/renderer/scripts/smh';
import store from '@/renderer/store';
type Handler = {
  templateColumns: any[];
  templateRecords: any[];
  excelHeaderMap: {
    [x: string]: string;
  };
  uploadInfo: {
    uploadFileName: string;
    uploadFilePath: string;
    fileSelected: boolean;
    fileValidating: boolean;
    fileValidated: boolean;
    uploadPercent: number;
    uploadToCOS: boolean;
    canCancel: boolean;
  };
  task: {
    checkTaskId: number;
    uploadTaskId: number;
    taskTimer: number;
  };
  resultColumns: any[];
  resultList: Ref<CoFile.ResultMap[]>;
  beforeUpload: (file: File) => boolean;
  initUpload: () => void;
  cancelCheck: () => Promise<void>;
  cancelUpload: () => void;
  confrimImport: () => Promise<any>;
};
export default function importHandler(): Handler {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  // 模板表格
  const templateColumns = [
    {
      colKey: 'nickname',
      title: i18n_t('用户昵称（必填，可重复，不可超过100字符）'),
      width: 100,
      ellipsis: true,
    },
    store.getters.onlyPasswordLogin
      ? {
          colKey: 'accountName',
          title: i18n_t('用户账号'),
          ellipsis: true,
        }
      : {
          colKey: 'phoneNumber',
          title: i18n_t('手机号（必填，不可重复，非内地手机号需加区号前缀）'),
          ellipsis: true,
        },
    {
      colKey: 'space',
      title: i18n_t('个人空间分配（选填，单位：GB，只支持正整数，不填写或填0不分配个人空间）'),
      width: 180,
      ellipsis: true,
    },
    {
      colKey: 'group',
      title: i18n_t('所属团队（层级以“/”划分，选填，可新增团队，每一级团队名称不可超过64字符）'),
      width: 210,
      ellipsis: true,
    },
  ];
  const templateRecords = [
    {
      index: 0,
      nickname: i18n_t('张三'),
      accountName: 'zhangsan666',
      phoneNumber: '18700000000',
      space: 20,
      group: i18n_t('一级部门/二级部门'),
    },
    {
      index: 1,
      nickname: i18n_t('李四'),
      accountName: 'lisi8888',
      phoneNumber: '+852-66502386',
      space: 0,
      group: i18n_t('一级部门/二级部门/三级部门'),
    },
  ];

  // 上传参数
  const uploadInfo = reactive({
    uploadFilePath: '',
    uploadFileName: '',
    fileSelected: false,
    fileValidating: false,
    fileValidated: false,
    uploadPercent: 0,
    uploadToCOS: false,
    canCancel: false,
  });

  // 异步任务
  const task = reactive({
    checkTaskId: -1,
    uploadTaskId: -1,
    taskTimer: 0,
  });
  // 异步任务查询间隔 5s
  const QueryTaskInterval = 5000;

  // 导入结果表格
  const resultColumns = [
    {
      colKey: 'index',
      title: i18n_t('行数'),
      width: 50,
    },
    {
      colKey: 'name',
      title: i18n_t('用户昵称'),
      ellipsis: true,
    },
    store.getters.onlyPasswordLogin
      ? {
          colKey: 'accountName',
          title: i18n_t('用户账号'),
          ellipsis: true,
        }
      : {
          colKey: 'phoneNumber',
          title: i18n_t('手机号'),
          ellipsis: true,
        },
    {
      colKey: 'personalSpaceQuota',
      title: i18n_t('个人空间分配（GB）'),
      width: 100,
      ellipsis: true,
    },
    {
      colKey: 'team',
      title: i18n_t('所属团队'),
      ellipsis: true,
    },
    {
      colKey: 'message',
      title: i18n_t('失败原因'),
      ellipsis: true,
    },
  ];
  const excelHeaderMap = {
    name: 'Name',
    phoneNumber: 'Phone',
    personalSpaceQuota: 'Personal capacity',
    team: 'Enterprise Sector',
  };
  const resultList = ref([]);
  const methods = {
    // 获取文件上传body
    getFileBody(file: File) {
      return new Promise(resolve => {
        const reads = new FileReader();
        reads.readAsArrayBuffer(file);
        reads.onload = async (e: Event) => {
          const body = (e.target as any).result;
          resolve(body);
        };
      });
    },
    // 每5秒查询一次异步任务返回结果
    async queryTask(taskId: string, callback?: Function): Promise<any> {
      const query = async () => {
        let result;
        try {
          const res = await smh.userAPI.queryTask({
            taskIdList: taskId,
          });
          [result] = res.data;
        } catch (e) {
          // 偶现查询出错，认为任务没有结束，下次轮询重试
          result = {
            status: -1,
            remaining: -1,
          };
        }
        return result;
      };

      // 异步任务结束标志
      // 202: 任务进行中；200: 任务成功完成且有返回结果，返回结果在 result 字段中，204: 任务成功完成且无返回结果，500: 任务执行失败；499: 用户取消;
      const isTaskFinished = (res: { status: number; remaining: number }) => {
        if ([200, 204, 499, 500].includes(res.status) || res.remaining === 0) {
          return true;
        }
        return false;
      };
      return new Promise(resolve => {
        const queryFn = async () => {
          const res = await query();
          if (isTaskFinished(res)) {
            resolve(res);
          } else {
            task.taskTimer && window.clearTimeout(task.taskTimer);
            if (res.status === 202 || res.status === -1) {
              callback && callback(res);
              task.taskTimer = window.setTimeout(() => {
                queryFn();
              }, QueryTaskInterval);
            }
          }
        };
        queryFn();
      });
    },
    // 文件校验任务
    queryCheckTask(taskId: string) {
      return methods.queryTask(taskId);
    },
    // 导入任务
    queryUploadTask(taskId: string) {
      // 导入进度通过查询异步任务已经完成的数量来计算
      const uploading = (res: SMH.TaskResultItem) => {
        const { total = 0, remaining = 0 } = res;
        const finished = total - remaining;
        if (total) {
          uploadInfo.uploadPercent = Math.floor((finished / total) * 100);
        }
      };
      return methods.queryTask(taskId, uploading);
    },
    // 取消任务
    cancelTask() {
      window.clearTimeout(task.taskTimer);
      task.taskTimer = 0;
    },
    // 校验文件过程
    async checkFile() {
      try {
        tips.loading(i18n_t('正在校验文件内容'), 0);
        const res = await smh.userAPI.checkXlsx({
          filePath: uploadInfo.uploadFilePath,
        });
        const { taskId } = res.data;
        if (taskId) {
          // 异步任务查询
          task.checkTaskId = taskId;
          uploadInfo.canCancel = true;
          const checkResult = await methods.queryCheckTask(`${taskId}`);
          uploadInfo.canCancel = false;
          if (checkResult.status === 200) {
            tips.success(i18n_t('文件内容正确'), 2000);
            uploadInfo.fileValidated = true;
          } else if (checkResult.status !== 499) {
            tips.error(i18n_t('文件内容不正确'), 2000);
            uploadInfo.fileValidated = false;
          }
        } else {
          // 同步查询结果
          const { result } = res.data;
          const { status } = (result as SMH.AsyncTaskResult[])[0];
          if (status === 200) {
            tips.success(i18n_t('文件内容正确'), 2000);
            uploadInfo.fileValidated = true;
          } else if (status !== 499) {
            tips.error(i18n_t('文件内容不正确'), 2000);
            uploadInfo.fileValidated = false;
          }
        }
      } catch (err: any) {
        tips.error(err, 2000, i18n_t('文件内容错误'));
        uploadInfo.fileValidated = false;
      }
    },
    async upload(file: File) {
      tips.loading(i18n_t('准备上传'), 0);
      const ext = file.name.slice(file.name.lastIndexOf('.') + 1);
      uploadInfo.fileSelected = true;
      uploadInfo.uploadFileName = file.name;
      try {
        const xlsInfo = await smh.userAPI.getXlsxInfo({
          ext,
        });
        const { domain, headers, path } = xlsInfo.data;
        uploadInfo.uploadFilePath = path.startsWith('/') ? path.slice(1) : path;
        const body = await methods.getFileBody(file);
        const cosRequest = getRequest({
          logPrefix: 'UploadImportUsers',
        });
        await cosRequest({
          url: `${vm.$store.getters.protocol}://${domain}/${encodeURI(uploadInfo.uploadFilePath)}`,
          method: 'PUT',
          headers,
          data: body,
        });
        uploadInfo.uploadToCOS = true;
        await methods.checkFile();
        uploadInfo.fileValidating = false;
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    beforeUpload(file: UploadFile) {
      const filename = file.name as string;
      const lastIndex = filename.lastIndexOf('.');
      if (lastIndex < 0) {
        tips.error(i18n_t('格式不正确，请上传xls或xlsx格式'));
        return false;
      }
      const ext = filename.slice(lastIndex + 1).toLowerCase();
      if (!['xls', 'xlsx'].includes(ext as string)) {
        tips.error(i18n_t('格式不正确，请上传xls或xlsx格式'));
        return false;
      }
      methods.initUpload();
      uploadInfo.fileValidating = true;
      methods.upload(file.raw as File);
      // 这里直接返回true，校验过程在upload中异步执行
      return true;
    },
    // 取消校验
    async cancelCheck() {
      try {
        await smh.userAPI.cancelTask({
          taskId: task.checkTaskId,
        });
        methods.cancelTask();
        methods.initUpload();
        tips.success(i18n_t('取消上传'));
      } catch (e) {
        tips.error(i18n_t('取消失败'));
      }
    },
    // 取消导入
    async cancelUpload() {
      try {
        const res = await smh.userAPI.cancelTask({
          taskId: task.uploadTaskId,
        });
        methods.cancelTask();
        const { taskId } = res.data;
        const uploadRes = await methods.queryUploadTask(`${taskId}`);
        resultList.value = uploadRes.result;
      } catch (e) {
        resultList.value = [];
      }
    },
    // 初始化上传参数
    initUpload() {
      Object.assign(uploadInfo, {
        uploadFileName: '',
        uploadFilePath: '',
        fileSelected: false,
        fileValidating: false,
        fileValidated: false,
        uploadPercent: 0,
        canCancel: false,
        uploadToCOS: false,
      });
      Object.assign(task, {
        checkTaskId: -1,
        uploadTaskId: -1,
        taskTimer: 0,
      });
      resultList.value = [];
    },
    // 点击导入
    async confrimImport() {
      let result = {};
      try {
        const res = await smh.userAPI.uploadXlsx({
          filePath: uploadInfo.uploadFilePath,
        });
        const { taskId } = res.data;
        if (taskId) {
          // 异步任务查询
          task.uploadTaskId = taskId;
          result = await methods.queryUploadTask(`${taskId}`);
        } else {
          // 同步查询结果
          uploadInfo.uploadPercent = 100;
          result = res.data;
        }
      } catch (e) {
        //
      }
      return result;
    },
  };
  return {
    templateColumns,
    templateRecords,
    excelHeaderMap,
    uploadInfo,
    task,
    resultColumns,
    resultList,
    ...methods,
  };
}
